import { inject, Injectable, signal } from '@angular/core';
import { UserPositionFacade } from '@pf/platform-core/domain';
import { CpwTaskStateService } from '../infrastructure/cpw-task-state.service';
import { LoggerService } from '@pf/shared-services';
import { RoutesForReceiving } from './receiving.routes';
import { distinctUntilChanged, map, Observable, of, switchMap, throwError } from 'rxjs';
import {
    ExceptionDto,
    ExceptionFacade,
    InventoryBundleDto,
    InventoryBundleFacade,
    ReceiptHeaderDto,
    ReceiptHeaderFacade,
    ReceiptLineDto,
    ReceiptLineFacade,
    WarehouseFacade,
} from '@pf/platform-wms/domain';
import {
    ExceptionStatus,
    InventoryBundleCondition,
    ReceiptStatus,
    WmsPlatformEntity,
} from '@control-tower/platform-wms';
import { RmaContext, RmaItem } from '../models/rma-context.model';
import { RmaTask } from '../models/rma-task.model';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { PhotoCaptureFile } from '../models/photo-capture.file';
import { CpwExceptionType } from '../models/cpw-exception-types';
import {
    RMA_INTAKE_CHANNEL_KEY,
    RMA_TASK_ID,
} from '../constants/rma.constants';
import { NoteDto } from '@pf/shared/util-platform';
import { DocumentDto, DocumentRequirementLevelType } from '@pf/shared-common';

export interface ReceiveRmaItemParams {
    itemId: string;
    quantity: number;
    orderNumber?: string | null;
    notes?: string | null;
    linkedProjectId?: string | null;
    containerId: string;
}

export interface CreateRmaQuarantineExceptionParams {
    attemptedScan: string;
    quantity: number;
    orderNumber?: string | null;
    notes?: string | null;
    quarantineSlotId: string;
    photos: PhotoCaptureFile[];
}

@UntilDestroy()
@Injectable()
export class RmaFacade {
    private readonly taskStateService = inject(CpwTaskStateService);
    private readonly userPositionFacade = inject(UserPositionFacade);
    private readonly warehouseFacade = inject(WarehouseFacade);
    private readonly receiptHeaderFacade = inject(ReceiptHeaderFacade);
    private readonly receiptLineFacade = inject(ReceiptLineFacade);
    private readonly exceptionFacade = inject(ExceptionFacade);
    private readonly inventoryBundleFacade = inject(InventoryBundleFacade);
    private readonly routes = inject(RoutesForReceiving);
    private readonly logger = inject(LoggerService);

    /** Emits the current active RMA task whenever it changes. */
    activeRmaTask$ = this.taskStateService.activeTask$.pipe(
        map(task => (task?.id === RMA_TASK_ID ? (task as RmaTask) : null)),
        distinctUntilChanged(),
        untilDestroyed(this)
    );

    /** Holds RMA items entered during the current receiving session. */
    readonly activeRmaItems = signal<RmaItem[]>([]);

    /** Navigates to the standalone RMA receiving flow. */
    navigateToRma() {
        this.setActiveRmaTask();
        this.routes.rma().navigate();
    }

    /** Creates and registers an RMA task in the task state service. */
    setActiveRmaTask(context: RmaContext = {}) {
        const task: RmaTask = {
            id: RMA_TASK_ID,
            context,
            details: '',
            group: '',
            priority: false,
            readonly: false,
            taskIconType: 'receiving',
            time: new Date(),
            title: 'RMA Receiving',
            warehouseId: this.userPositionFacade.active?.locationId ?? '',
            claimedAt: new Date(),
        };
        this.taskStateService.clearTasks();
        this.taskStateService.setTasks([task]);
        this.taskStateService.setActiveTask(task.id);
    }

    /** Merges a partial RMA context patch through the task state service. */
    updateRmaContext$(patch: Partial<RmaContext>): Observable<unknown> {
        return this.taskStateService.updateTaskContext$<RmaContext>(
            RMA_TASK_ID,
            patch
        );
    }

    /** Clears the active RMA task and removes context from the user position. */
    clearRmaContext$(): Observable<unknown> {
        this.taskStateService.clearTasks();
        return this.userPositionFacade.updateContext$({
            captureData: null,
            notFoundData: null,
            quarantineDone: null,
            createdReceiptId: null,
            stagedContainerLabel: null,
            rma: null,
        });
    }

    /** Creates a receipt header and line for an RMA item (does not receive). */
    createRmaReceipt$(
        params: ReceiveRmaItemParams
    ): Observable<{ headerId: string; lineId: string }> {
        return this.createRmaReceiptHeader$(params).pipe(
            switchMap(savedHeader =>
                this.createRmaReceiptLine$(savedHeader.id, params).pipe(
                    map(savedLine => ({
                        headerId: savedHeader.id,
                        lineId: savedLine.id,
                    }))
                )
            )
        );
    }

    /** Marks a bundle's condition as Returned (for RMA items after putaway). */
    markBundleAsReturned$(bundleId: string): Observable<InventoryBundleDto> {
        return this.inventoryBundleFacade.updateCondition$(
            bundleId,
            InventoryBundleCondition.Returned
        );
    }

    createQuarantineExceptionForUnknownItem$(
        params: CreateRmaQuarantineExceptionParams
    ): Observable<ExceptionDto> {
        const exception = this.buildQuarantineException(params);
        const docs = this.mapPhotosToDocuments(params.photos);
        return this.exceptionFacade
            .save$(exception)
            .pipe(
                switchMap(savedException =>
                    docs.length
                        ? this.exceptionFacade
                              .saveDocuments$(savedException.id, docs)
                              .pipe(map(() => savedException))
                        : of(savedException)
                )
            );
    }

    /** Adds an RMA item to the active session queue. */
    addRmaItem(item: RmaItem) {
        this.activeRmaItems.update(items => [...items, item]);
        // Persist RMA items in context
        this.updateRmaContext$({ rmaItems: this.activeRmaItems() }).subscribe();
    }

    /** Clears all pending RMA items from the session. */
    clearRmaItems() {
        this.activeRmaItems.set([]);
        // Clear RMA items from context
        this.updateRmaContext$({ rmaItems: [] }).subscribe();
    }

    private createRmaReceiptHeader$(params: ReceiveRmaItemParams) {
        const warehouseId = this.warehouseFacade.active?.id;
        if (!warehouseId) {
            return throwError(
                () => new Error('No active warehouse found. Please select a warehouse.')
            );
        }

        const customFields = [
            ...(params.notes
                ? [{ key: 'receiverNotes', value: params.notes }]
                : []),
            ...(params.linkedProjectId
                ? [{ key: 'linkedProjectId', value: params.linkedProjectId }]
                : []),
        ];

        return this.receiptHeaderFacade.save$(
            new ReceiptHeaderDto({
                id: '',
                poNumber: params.orderNumber ?? null,
                warehouse: { id: warehouseId },
                status: ReceiptStatus.Pending,
                customFields,
            } as Partial<ReceiptHeaderDto>)
        );
    }

    private createRmaReceiptLine$(
        headerId: string,
        params: ReceiveRmaItemParams
    ) {
        return this.receiptLineFacade.save$(
            new ReceiptLineDto({
                id: '',
                inventoryReceiptId: headerId,
                status: ReceiptStatus.Pending,
                expectedQuantity: params.quantity,
                actualQuantity: params.quantity,
                item: { id: params.itemId },
                vendorLPN: 'RMA',
            } as Partial<ReceiptLineDto>)
        );
    }

    private buildQuarantineException(
        params: CreateRmaQuarantineExceptionParams
    ): ExceptionDto {
        const warehouseId = this.warehouseFacade.active?.id;
        if (!warehouseId) {
            throw new Error('No active warehouse found. Please select a warehouse.');
        }
        return new ExceptionDto({
            status: ExceptionStatus.Open,
            sourceEntity: WmsPlatformEntity.Warehouse,
            sourceEntityId: warehouseId,
            exceptionType: { id: CpwExceptionType.ReceivingMissingItemCatalog },
            warehouse: { id: warehouseId },
            slot: { id: params.quarantineSlotId },
            actualQuantity: params.quantity,
            notes: this.buildQuarantineNotes(params),
            customFields: [
                { key: 'attemptedItemScan', value: params.attemptedScan },
                { key: RMA_INTAKE_CHANNEL_KEY, value: 'RMA_QUARANTINE' },
                ...(params.orderNumber
                    ? [
                          {
                              key: 'optionalOrderNumber',
                              value: params.orderNumber,
                          },
                      ]
                    : []),
            ],
        });
    }

    private buildQuarantineNotes(
        params: CreateRmaQuarantineExceptionParams
    ): NoteDto[] {
        return [
            new NoteDto({
                note: [
                    `Attempted Item Scan: ${params.attemptedScan}`,
                    params.orderNumber
                        ? `Order Number: ${params.orderNumber}`
                        : null,
                    params.notes ? `Receiver Notes: ${params.notes}` : null,
                ]
                    .filter(Boolean)
                    .join('\n'),
            }),
        ];
    }

    private mapPhotosToDocuments(photos: PhotoCaptureFile[]): DocumentDto[] {
        return photos.map(
            photo =>
                new DocumentDto({
                    file: photo.file,
                    fileName: photo.name,
                    documentTypeId: '',
                    documentTypeName: '',
                    requirementLevel: DocumentRequirementLevelType.Required,
                })
        );
    }
}
